import React, { useState, useMemo, useEffect } from 'react';

interface Column {
  key: string;
  label: string;
  type: string;
  isPrimary?: boolean;
  isLocked?: boolean;
  category?: string;
}

interface ColumnSelectorPanelProps {
  isOpen: boolean;
  onClose: () => void;
  allColumns: Column[];
  visibleColumns: string[];
  onApply: (selectedColumns: string[]) => void;
}

interface CategoryGroup {
  name: string;
  icon: string;
  columns: Column[];
}

export const ColumnSelectorPanel: React.FC<ColumnSelectorPanelProps> = ({
  isOpen,
  onClose,
  allColumns,
  visibleColumns,
  onApply
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedColumns, setSelectedColumns] = useState<string[]>(visibleColumns);
const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
  new Set(['career', 'skills', 'education', 'profile', 'company', 'location', 'custom'])
);

  const [showAllInCategory, setShowAllInCategory] = useState<Set<string>>(new Set());

  // Sync selectedColumns when visibleColumns changes or panel opens
  useEffect(() => {
    if (isOpen) {
      setSelectedColumns(visibleColumns);
    }
  }, [isOpen, visibleColumns]);

  // Group columns by category
  const columnGroups = useMemo(() => {
  // Locked columns are truly always-visible (e.g. 'name' on candidates,
  // 'company_name' on companies). Primary columns are visible by default
  // but can be toggled off.
  const lockedColumns = allColumns.filter(col => col.isLocked);
  const primaryColumns = allColumns.filter(col => col.isPrimary && !col.isLocked);

  const groups: Record<string, CategoryGroup> = {
    career: {
      name: 'Career History',
      icon: '💼',
      columns: allColumns.filter(col => col.category === 'career')
    },
    education: {
      name: 'Education & Certifications',
      icon: '🎓',
      columns: allColumns.filter(col => col.category === 'education')
    },
    skills: {
      name: 'Skills',
      icon: '⚡',
      columns: allColumns.filter(col => col.category === 'skills')
    },
    profile: {
      name: 'Profile Metrics',
      icon: '👤',
      columns: allColumns.filter(col => col.category === 'profile')
    },
    company: {
      name: 'Company Info',
      icon: '🏢',
      columns: allColumns.filter(col => col.category === 'company')
    },
    location: {
      name: 'Location',
      icon: '📍',
      columns: allColumns.filter(col => col.category === 'location')
    },
    custom: {
      name: 'Custom Columns',
      icon: '🔧',
      columns: allColumns.filter(col => (col as any).isCustom)
    }
  };
  
  return { lockedColumns, primaryColumns, groups };
}, [allColumns]);

  // Filter columns by search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm) return columnGroups.groups;

    const filtered: Record<string, CategoryGroup> = {};
    const lowerSearch = searchTerm.toLowerCase();

    Object.entries(columnGroups.groups).forEach(([key, group]) => {
      const matchingColumns = group.columns.filter(col =>
        col.label.toLowerCase().includes(lowerSearch) ||
        col.key.toLowerCase().includes(lowerSearch)
      );

      if (matchingColumns.length > 0) {
        filtered[key] = {
          ...group,
          columns: matchingColumns
        };
      }
    });

    return filtered;
  }, [columnGroups.groups, searchTerm]);

  const handleToggleColumn = (columnKey: string, isLocked: boolean) => {
    if (isLocked) return; // Can't toggle locked columns (e.g. row identifier)

    setSelectedColumns(prev =>
      prev.includes(columnKey)
        ? prev.filter(k => k !== columnKey)
        : [...prev, columnKey]
    );
  };

  const handleToggleCategory = (categoryKey: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryKey)) {
        next.delete(categoryKey);
      } else {
        next.add(categoryKey);
      }
      return next;
    });
  };

  const handleShowAll = (categoryKey: string) => {
    setShowAllInCategory(prev => {
      const next = new Set(prev);
      if (next.has(categoryKey)) {
        next.delete(categoryKey);
      } else {
        next.add(categoryKey);
      }
      return next;
    });
  };

  const handleApply = () => {
    onApply(selectedColumns);
    onClose();
  };

  const handleReset = () => {
    // Reset to defaults: every locked + primary column visible, others hidden.
    const defaultKeys = [
      ...columnGroups.lockedColumns.map(col => col.key),
      ...columnGroups.primaryColumns.map(col => col.key)
    ];
    setSelectedColumns(defaultKeys);
  };

  const getVisibleColumnsInCategory = (columns: Column[], categoryKey: string) => {
    const showAll = showAllInCategory.has(categoryKey);
    return showAll ? columns : columns.slice(0, 5);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="slide-panel w-96">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200/80 flex items-center justify-between bg-gray-50/50">
          <h2 className="text-lg font-semibold text-gray-900 tracking-tight">Customize Columns</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-200/80">
          <input
            type="text"
            placeholder="Search columns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input"
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Locked Columns — always visible, can't be toggled */}
          {columnGroups.lockedColumns.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Always Visible
                </span>
              </div>
              <div className="space-y-2">
                {columnGroups.lockedColumns.map(col => (
                  <div
                    key={col.key}
                    className="flex items-center gap-2 text-sm text-gray-500 pl-2"
                  >
                    <span className="text-gray-400">🔒</span>
                    <span>{col.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Primary Columns — visible by default, toggleable */}
          {columnGroups.primaryColumns.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Default Columns
                </span>
                <span className="text-[11px] text-gray-400 normal-case">— shown by default; uncheck to hide</span>
              </div>
              <div className="space-y-1">
                {columnGroups.primaryColumns.map(col => {
                  const isSelected = selectedColumns.includes(col.key);
                  return (
                    <label
                      key={col.key}
                      className="flex items-center gap-2 text-sm text-gray-700 pl-2 py-1 rounded hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleColumn(col.key, false)}
                        className="h-4 w-4 accent-blue-600"
                      />
                      <span>{col.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Category Groups */}
          {Object.entries(filteredGroups).map(([categoryKey, group]) => {
            const isExpanded = expandedCategories.has(categoryKey);
            const visibleColumns = getVisibleColumnsInCategory(group.columns, categoryKey);
            const hasMore = group.columns.length > 5;
            const showingAll = showAllInCategory.has(categoryKey);

            return (
              <div key={categoryKey} className="mb-4">
                {/* Category Header */}
                <button
                  onClick={() => handleToggleCategory(categoryKey)}
                  className="flex items-center gap-2 w-full text-left py-2 hover:bg-gray-50 rounded"
                >
                  <span className="text-gray-600 transform transition-transform">
                    {isExpanded ? '▼' : '▶'}
                  </span>
                  <span className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                    {group.icon} {group.name}
                  </span>
                  <span className="ml-auto text-xs text-gray-500">
                    ({group.columns.filter(col => selectedColumns.includes(col.key)).length}/{group.columns.length})
                  </span>
                </button>

                {/* Category Columns */}
                {isExpanded && (
                  <div className="mt-2 space-y-1 pl-4">
                    {visibleColumns.map(col => (
                      <label
                        key={col.key}
                        className="flex items-center gap-2 py-1.5 px-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedColumns.includes(col.key)}
                          onChange={() => handleToggleColumn(col.key, col.isLocked || false)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{col.label}</span>
                      </label>
                    ))}

                    {/* Show More/Less */}
                    {hasMore && (
                      <button
                        onClick={() => handleShowAll(categoryKey)}
                        className="text-xs text-blue-600 hover:text-blue-700 pl-2 py-1"
                      >
                        {showingAll
                          ? '▲ Show less'
                          : `▼ Show ${group.columns.length - 5} more...`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200/80 flex gap-3 bg-gray-50/50">
          <button
            onClick={handleReset}
            className="btn-secondary flex-1"
          >
            Reset to Default
          </button>
          <button
            onClick={handleApply}
            className="btn-primary flex-1"
          >
            Apply Changes
          </button>
        </div>
      </div>
    </>
  );
};
